const focusInput = (el?: HTMLElement | null) => {
    const input = el as HTMLInputElement | null;
    if (!input || typeof input.focus !== 'function') return;
    input.focus();
    // Select in a microtask so the whole value is replaced by the next digit,
    // but never after the user has already typed or moved on (a delayed select
    // was highlighting mid-number and the following key replaced it).
    queueMicrotask(() => {
        if (document.activeElement === input && typeof input.select === 'function') {
            input.select();
        }
    });
};

const columnInput = (row: Element, col: string): HTMLInputElement | null =>
    row.querySelector<HTMLInputElement>(
        `[data-nav="${col}"] input:not([type="checkbox"]), [data-nav="${col}"] textarea`
    );

const rowNavInputs = (row: Element): HTMLInputElement[] =>
    [...row.querySelectorAll<HTMLElement>('[data-nav]')]
        .map((cell) => cell.querySelector<HTMLInputElement>('input:not([type="checkbox"]), textarea'))
        .filter((el): el is HTMLInputElement => !!el);

const searchInput = (row: Element): HTMLInputElement | null =>
    row.querySelector<HTMLInputElement>('input:not([type="checkbox"])');

const focusVertical = (row: Element, col: string | null, dir: 1 | -1) => {
    let cursor: Element | null = dir > 0 ? row.nextElementSibling : row.previousElementSibling;
    while (cursor) {
        if (cursor.classList.contains('autocomp')) {
            focusInput(searchInput(cursor));
            return;
        }
        const same = col ? columnInput(cursor, col) : null;
        if (same) {
            focusInput(same);
            return;
        }
        const qty = columnInput(cursor, 'qty');
        if (qty) {
            focusInput(qty);
            return;
        }
        const any = rowNavInputs(cursor)[0];
        if (any) {
            focusInput(any);
            return;
        }
        cursor = dir > 0 ? cursor.nextElementSibling : cursor.previousElementSibling;
    }
};

const focusHorizontal = (row: Element, index: number, delta: number) => {
    const cells = rowNavInputs(row);
    const next = cells[index + delta];
    if (next) {
        focusInput(next);
        return true;
    }
    let sibling: Element | null = delta > 0 ? row.nextElementSibling : row.previousElementSibling;
    while (sibling) {
        if (sibling.classList.contains('autocomp')) {
            focusInput(searchInput(sibling));
            return true;
        }
        const edge = rowNavInputs(sibling);
        const target = delta > 0 ? edge[0] : edge[edge.length - 1];
        if (target) {
            focusInput(target);
            return true;
        }
        sibling = delta > 0 ? sibling.nextElementSibling : sibling.previousElementSibling;
    }
    return false;
};

export const handleKeyDown = (event, opts?: { enterMovesDown?: boolean }) => {
    const current = event.target as HTMLElement | null;
    if (!current || typeof current.closest !== 'function') return;
    const currentRow = current.closest('tr');
    if (!currentRow) return;

    const navCell = current.closest('[data-nav]');
    if (navCell && currentRow.contains(navCell)) {
        const col = navCell.getAttribute('data-nav');
        const mine = navCell.querySelector('input, textarea');
        const ind = mine ? rowNavInputs(currentRow).indexOf(mine as HTMLInputElement) : -1;
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || (event.key === 'Enter' && opts?.enterMovesDown)) {
            event.preventDefault();
            event.stopPropagation();
            focusVertical(currentRow, col, event.key === 'ArrowUp' ? -1 : 1);
            return;
        }
        if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && ind >= 0) {
            event.stopPropagation();
            const moved = focusHorizontal(currentRow, ind, event.key === 'ArrowRight' ? -1 : 1);
            if (moved) event.preventDefault();
            return;
        }
        return;
    }

    event.stopPropagation();
    if (!(current instanceof HTMLInputElement)) return;
    const inputs = [...currentRow.getElementsByTagName('input')];
    const ind = inputs.indexOf(current);
    if (ind === -1) return;
    switch (event.key) {
        case 'ArrowRight':
            if (ind > 0) {
                event.preventDefault();
                focusInput(inputs[ind - 1]);
            } else {
                const prev = currentRow.previousElementSibling?.getElementsByTagName('input')[inputs.length - 1];
                if (prev) {
                    event.preventDefault();
                    focusInput(prev);
                }
            }
            break;
        case 'ArrowUp': {
            event.preventDefault();
            const prev = currentRow.previousElementSibling?.getElementsByTagName('input')[ind]
                ?? currentRow.previousElementSibling?.getElementsByTagName('input')[0];
            focusInput(prev);
            break;
        }
        case 'ArrowLeft':
            if (ind < inputs.length - 1) {
                event.preventDefault();
                focusInput(inputs[ind + 1]);
            } else {
                const next = currentRow.nextElementSibling?.getElementsByTagName('input')[0];
                if (next) {
                    event.preventDefault();
                    focusInput(next);
                }
            }
            break;
        case 'ArrowDown': {
            event.preventDefault();
            let next = currentRow.nextElementSibling?.getElementsByTagName('input')[ind] as HTMLInputElement | undefined;
            if (!next || next.type === 'checkbox')
                next = currentRow.nextElementSibling?.getElementsByTagName('input')[0] as HTMLInputElement | undefined;
            focusInput(next);
            break;
        }
        case 'Enter':
            if (opts?.enterMovesDown) {
                event.preventDefault();
                let next = currentRow.nextElementSibling?.getElementsByTagName('input')[ind] as HTMLInputElement | undefined;
                if (!next || next.type === 'checkbox')
                    next = currentRow.nextElementSibling?.getElementsByTagName('input')[0] as HTMLInputElement | undefined;
                focusInput(next);
            }
            break;
        default:
            break;
    }
};
